import {
  ReadLeadForm,
  ReadLeadFormSubmission,
  UpdateLeadForm,
} from '@tinct/core-api-models';
import {ApiClient} from '../api-client/api-client';
import {ApiCollectionResponse, GetCollectionQueryParams} from '../types';

export interface GetLandingPageLeadFormSubmissionsStaticParams {
  landingPageId: string;
}

export interface GetOrganizationLeadFormSubmissionsStaticParams {
  organizationId: string;
}

/**
 * Lead-capture form of a generated landing page. The config is a singleton
 * per page; the generator renders the form at generation time, so field or
 * enabled changes only appear on the next regeneration (successMessage and
 * destination apply live — the injected runtime reads current config).
 */
export class LeadFormsEndpoint {
  constructor(private client: ApiClient) {}

  /** Always 200 — returns disabled defaults when nothing is configured yet. */
  async getLeadForm(landingPageId: string): Promise<ReadLeadForm> {
    return this.client.getById<ReadLeadForm>(
      `/api/v1/landing-pages/${landingPageId}/lead-form`,
    );
  }

  async updateLeadForm(
    landingPageId: string,
    data: UpdateLeadForm,
  ): Promise<ReadLeadForm> {
    return this.client.put<ReadLeadForm>(
      `/api/v1/landing-pages/${landingPageId}/lead-form`,
      data,
    );
  }

  async deleteLeadForm(landingPageId: string): Promise<void> {
    return this.client.delete<void>(
      `/api/v1/landing-pages/${landingPageId}/lead-form`,
    );
  }

  /** The page's submissions, newest first. */
  async getLandingPageLeadFormSubmissions(
    params: GetCollectionQueryParams<
      ReadLeadFormSubmission,
      GetLandingPageLeadFormSubmissionsStaticParams
    >,
  ): Promise<ApiCollectionResponse<ReadLeadFormSubmission>> {
    const { staticParams, fields, offset = 0, limit = 20, filters } = params;

    return this.client.getCollection<ReadLeadFormSubmission>(
      `/api/v1/landing-pages/${staticParams?.landingPageId}/lead-form/submissions`,
      fields,
      {
        offset,
        limit,
        ...filters,
      },
    );
  }

  /** All submissions of the workspace, newest first. */
  async getOrganizationLeadFormSubmissions(
    params: GetCollectionQueryParams<
      ReadLeadFormSubmission,
      GetOrganizationLeadFormSubmissionsStaticParams
    >,
  ): Promise<ApiCollectionResponse<ReadLeadFormSubmission>> {
    const { staticParams, fields, offset = 0, limit = 20, filters } = params;

    return this.client.getCollection<ReadLeadFormSubmission>(
      `/api/v1/organizations/${staticParams?.organizationId}/lead-form-submissions`,
      fields,
      {
        offset,
        limit,
        ...filters,
      },
    );
  }
}
